/**
 * Full D&D 5e character sheet: extra data stored in characters.details (JSONB), plus derived math
 * (spell DC, passives, carrying capacity) and rest rules. Pure functions, shared by server and web.
 */
import { abilityMod, checkModifier, isAbility, isSkill } from "./dnd.js";
export const CONDITIONS = [
    "blinded", "charmed", "deafened", "frightened", "grappled", "incapacitated", "invisible",
    "paralyzed", "petrified", "poisoned", "prone", "restrained", "stunned", "unconscious",
];
export const isCondition = (v) => typeof v === "string" && CONDITIONS.includes(v);
export const HIT_DIE_SIDES = [6, 8, 10, 12];
// ------------------------------------------------------------------ normalisation
const str = (v, max = 200) => (typeof v === "string" ? v.slice(0, max) : "");
const int = (v, min, max, fallback = 0) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};
const num = (v, min, max) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n * 100) / 100)) : 0;
};
const bool = (v) => v === true;
const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const arr = (v, max) => (Array.isArray(v) ? v.slice(0, max) : []);
let idCounter = 0;
export const newId = () => `${Date.now().toString(36)}${(idCounter++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const id = (v) => (typeof v === "string" && v ? v.slice(0, 40) : newId());
export function emptyDetails() {
    return normalizeDetails({});
}
/**
 * Clamp/repair untrusted details (from the DB or a client). Unknown keys are dropped, missing ones get defaults,
 * so characters created before the full sheet existed load fine. `legacyInventory` seeds equipment once.
 */
export function normalizeDetails(input, legacyInventory = []) {
    const d = obj(input);
    const sp = obj(d.speeds);
    const prof = obj(d.proficiencies);
    const sc = obj(d.spellcasting);
    const pact = obj(sc.pact);
    const cur = obj(d.currency);
    const pers = obj(d.personality);
    const slotsIn = arr(sc.slots, 9);
    const equipmentIn = Array.isArray(d.equipment)
        ? arr(d.equipment, 300)
        : legacyInventory.map((name) => ({ name, qty: 1 }));
    return {
        background: str(d.background, 80),
        alignment: str(d.alignment, 40),
        subclass: str(d.subclass, 80),
        multiclass: arr(d.multiclass, 6).map((m) => ({ className: str(obj(m).className, 60), level: int(obj(m).level, 1, 20, 1) })),
        xp: int(d.xp, 0, 10_000_000),
        portraitUrl: /^https?:\/\//.test(str(d.portraitUrl, 500)) ? str(d.portraitUrl, 500) : "",
        initiativeMisc: int(d.initiativeMisc, -20, 20),
        speeds: { fly: int(sp.fly, 0, 500), swim: int(sp.swim, 0, 500), climb: int(sp.climb, 0, 500) },
        hitDice: arr(d.hitDice, 4)
            .map((h) => {
            const o = obj(h);
            const total = int(o.total, 0, 20);
            return { die: HIT_DIE_SIDES.includes(Number(o.die)) ? Number(o.die) : 8, total, remaining: int(o.remaining, 0, total, total) };
        }),
        inspiration: bool(d.inspiration),
        exhaustion: int(d.exhaustion, 0, 6),
        conditions: [...new Set(arr(d.conditions, 20).filter(isCondition))],
        proficiencies: {
            armor: str(prof.armor, 500), weapons: str(prof.weapons, 500), tools: str(prof.tools, 500), languages: str(prof.languages, 500),
        },
        expertise: [...new Set(arr(d.expertise, 18).filter(isSkill))],
        jackOfAllTrades: bool(d.jackOfAllTrades),
        attacks: arr(d.attacks, 40).map((a) => {
            const o = obj(a);
            return {
                id: id(o.id),
                name: str(o.name, 80),
                ability: isAbility(o.ability) ? o.ability : o.ability === "none" ? "none" : "str",
                proficient: o.proficient === undefined ? true : bool(o.proficient),
                attackBonusOverride: o.attackBonusOverride === null || o.attackBonusOverride === undefined || o.attackBonusOverride === ""
                    ? null : int(o.attackBonusOverride, -20, 30),
                damageDice: str(o.damageDice, 20),
                damageType: str(o.damageType, 30),
                addModToDamage: o.addModToDamage === undefined ? true : bool(o.addModToDamage),
                range: str(o.range, 40),
                notes: str(o.notes, 300),
            };
        }),
        spellcasting: {
            ability: isAbility(sc.ability) ? sc.ability : null,
            slots: Array.from({ length: 9 }, (_, i) => {
                const o = obj(slotsIn[i]);
                const max = int(o.max, 0, 9);
                return { max, used: int(o.used, 0, max) };
            }),
            pact: (() => {
                const max = int(pact.max, 0, 4);
                return { level: int(pact.level, 0, 5), max, used: int(pact.used, 0, max) };
            })(),
            spells: arr(sc.spells, 300).map((x) => {
                const o = obj(x);
                return {
                    id: id(o.id), level: int(o.level, 0, 9), name: str(o.name, 80), prepared: bool(o.prepared), school: str(o.school, 30),
                    castingTime: str(o.castingTime, 40), range: str(o.range, 40), components: str(o.components, 300),
                    duration: str(o.duration, 60), concentration: bool(o.concentration), ritual: bool(o.ritual),
                    description: str(o.description, 8000),
                };
            }),
        },
        features: arr(d.features, 200).map((f) => {
            const o = obj(f);
            const maxUses = o.maxUses === null || o.maxUses === undefined || o.maxUses === "" ? null : int(o.maxUses, 0, 999);
            return {
                id: id(o.id),
                kind: o.kind === "race" || o.kind === "feat" ? o.kind : "class",
                name: str(o.name, 80),
                description: str(o.description, 4000),
                maxUses,
                used: maxUses === null ? 0 : int(o.used, 0, maxUses),
                recharge: o.recharge === "short" || o.recharge === "long" ? o.recharge : "none",
            };
        }),
        equipment: equipmentIn.map((x) => {
            const o = obj(x);
            return {
                id: id(o.id), name: str(o.name, 120), qty: int(o.qty, 0, 9999, 1), weight: num(o.weight, 0, 9999),
                equipped: bool(o.equipped), attuned: bool(o.attuned), notes: str(o.notes, 300),
            };
        }),
        currency: {
            cp: int(cur.cp, 0, 10_000_000), sp: int(cur.sp, 0, 10_000_000), ep: int(cur.ep, 0, 10_000_000),
            gp: int(cur.gp, 0, 10_000_000), pp: int(cur.pp, 0, 10_000_000),
        },
        personality: {
            traits: str(pers.traits, 2000), ideals: str(pers.ideals, 2000), bonds: str(pers.bonds, 2000), flaws: str(pers.flaws, 2000),
            appearance: str(pers.appearance, 2000), backstory: str(pers.backstory, 10_000), allies: str(pers.allies, 4000),
        },
    };
}
export const totalLevel = (level, d) => Math.min(20, level + d.multiclass.reduce((s, m) => s + m.level, 0));
export function spellSaveDc(s) {
    const a = s.details.spellcasting.ability;
    return a ? 8 + s.proficiencyBonus + abilityMod(s.abilities[a]) : null;
}
export function spellAttackBonus(s) {
    const a = s.details.spellcasting.ability;
    return a ? s.proficiencyBonus + abilityMod(s.abilities[a]) : null;
}
/** Passive score = 10 + the skill's check modifier (expertise / JoAT included). */
export function passiveScore(s, skill) {
    return 10 + checkModifier(s, { kind: "skill", key: skill });
}
export const carryingCapacity = (str) => str * 15;
export function totalWeight(items) {
    return Math.round(items.reduce((sum, i) => sum + i.qty * i.weight, 0) * 100) / 100;
}
export const attunedCount = (items) => items.filter((i) => i.attuned).length;
export const MAX_ATTUNEMENT = 3;
export function attackBonus(s, a) {
    if (a.attackBonusOverride !== null)
        return a.attackBonusOverride;
    const mod = a.ability === "none" ? 0 : abilityMod(s.abilities[a.ability]);
    return mod + (a.proficient ? s.proficiencyBonus : 0);
}
/** Damage formula for the dice roller, e.g. "1d8+3". Null if the dice aren't a simple NdX. */
export function damageFormula(s, a) {
    const m = /^\s*(\d*)d(\d+)\s*([+-]\s*\d+)?\s*$/i.exec(a.damageDice);
    if (!m)
        return null;
    const count = m[1] ? Number(m[1]) : 1;
    const flat = m[3] ? Number(m[3].replace(/\s/g, "")) : 0;
    const mod = (a.addModToDamage && a.ability !== "none" ? abilityMod(s.abilities[a.ability]) : 0) + flat;
    return `${count}d${m[2]}${mod > 0 ? `+${mod}` : mod < 0 ? mod : ""}`;
}
const resetUses = (features, recharges) => features.map((f) => (recharges.includes(f.recharge) ? { ...f, used: 0 } : f));
/** Short rest: short-rest feature uses and pact slots come back. Hit dice are spent separately. */
export function shortRest(c) {
    const sc = c.details.spellcasting;
    return {
        ...c,
        details: {
            ...c.details,
            features: resetUses(c.details.features, ["short"]),
            spellcasting: { ...sc, pact: { ...sc.pact, used: 0 } },
        },
    };
}
/**
 * Long rest: full HP, temp HP and death saves cleared, regain half your total hit dice (min 1, largest dice first),
 * all spell/pact slots and short+long feature uses restored, exhaustion reduced by 1.
 */
export function longRest(c) {
    const sc = c.details.spellcasting;
    const totalHd = c.details.hitDice.reduce((s, h) => s + h.total, 0);
    let regain = totalHd > 0 ? Math.max(1, Math.floor(totalHd / 2)) : 0;
    const order = c.details.hitDice.map((h, i) => ({ h, i })).sort((a, b) => b.h.die - a.h.die);
    const hitDice = c.details.hitDice.map((h) => ({ ...h }));
    for (const { i } of order) {
        const h = hitDice[i];
        const add = Math.min(regain, h.total - h.remaining);
        h.remaining += add;
        regain -= add;
    }
    return {
        ...c,
        currentHp: c.maxHp,
        tempHp: 0,
        deathSaves: { successes: 0, failures: 0 },
        details: {
            ...c.details,
            hitDice,
            exhaustion: Math.max(0, c.details.exhaustion - 1),
            features: resetUses(c.details.features, ["short", "long"]),
            spellcasting: {
                ...sc,
                slots: sc.slots.map((s) => ({ ...s, used: 0 })),
                pact: { ...sc.pact, used: 0 },
            },
        },
    };
}
/** HP regained from one hit die: the roll + CON modifier, at least 0 (capped at max HP by the caller). */
export function hitDieHealing(roll, con) {
    return Math.max(0, roll + abilityMod(con));
}
/**
 * Spend a spell slot of `level` (1-9). Uses a regular slot if one is left, otherwise a pact slot of at least that
 * level (or the pact slot directly with `pact`). Returns null if nothing is left.
 */
export function spendSlot(d, level, pact = false) {
    const sc = d.spellcasting;
    const slot = sc.slots[level - 1];
    if (!pact && slot && slot.used < slot.max) {
        const slots = sc.slots.map((s, i) => (i === level - 1 ? { ...s, used: s.used + 1 } : s));
        return { ...d, spellcasting: { ...sc, slots } };
    }
    if (sc.pact.max > 0 && sc.pact.used < sc.pact.max && (pact || sc.pact.level >= level)) {
        return { ...d, spellcasting: { ...sc, pact: { ...sc.pact, used: sc.pact.used + 1 } } };
    }
    return null;
}
//# sourceMappingURL=sheet.js.map